import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { CrmCityDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { cn } from '@upme/ui/lib/utils';
import { CalendarDays } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/auth-context';
import {
  buildEventDateFilters,
  type EventPeriod,
  formatCrmEventDate,
  matchesEventPeriod,
} from '../lib/event-period';
import { crmEventsOptions } from '../queries';
import { CrmBoardToolbar } from './crm-board-toolbar';
import { CrmOrganizerPanel } from './crm-organizer-panel';

export function CrmEvents({ cityId, cities }: { cityId?: string; cities: ReadonlyArray<CrmCityDto> }) {
  const roles = useAuth().adminRoles;
  const canOpenEventAdmin = roles.includes('SUPERADMIN') || roles.includes('CITY_MODERATOR');
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const [period, setPeriod] = useState<EventPeriod>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const query = useQuery(crmEventsOptions(cityId));
  const timeZone = cities.find((city) => city.id === cityId)?.timezone ?? 'Asia/Tbilisi';
  const filters = buildEventDateFilters(query.data?.items ?? [], timeZone, now);
  const activePeriod = filters.some((filter) => filter.key === period) ? period : 'all';
  const items =
    query.data?.items.filter((event) => matchesEventPeriod(event, activePeriod, timeZone, now)) ?? [];
  const selectedId = items.some((event) => event.id === selected) ? selected : null;
  return (
    <div className="relative min-w-0 space-y-4">
      <CrmBoardToolbar
        eventsMode
        filters={{ segment: 'all', ...(cityId ? { cityId } : {}) }}
        cities={cities}
      />
      <div className="flex flex-wrap gap-2">
        {filters.map(({ key, label }) => (
          <Button
            key={key}
            variant={activePeriod === key ? 'default' : 'outline'}
            aria-pressed={activePeriod === key}
            onClick={() => {
              setPeriod(key);
              setSelected(null);
            }}
          >
            {label}
          </Button>
        ))}
      </div>
      {query.isPending ? (
        <p>Загрузка ивентов…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>Не удалось загрузить ивенты.</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Повторить
          </Button>
        </div>
      ) : (
        <div
          className={cn('min-w-0', selectedId && 'lg:grid lg:grid-cols-[min(35.125rem,45%)_minmax(0,1fr)]')}
        >
          <div
            className={cn(
              'min-w-0 flex-1 lg:max-h-[max(16rem,calc(var(--crm-viewport-h)-8.5rem))] lg:overflow-y-auto',
              selectedId && 'max-lg:hidden',
            )}
          >
            {items.length === 0 ? (
              <p className="text-muted-foreground">Ближайших ивентов за этот период нет.</p>
            ) : (
              <ul className="grid gap-3">
                {items.map((event) => (
                  <li
                    key={event.id}
                    className={cn(
                      'flex items-start gap-4 rounded-md border border-border/60 p-4',
                      selectedId === event.id && 'border-primary/50',
                    )}
                  >
                    {event.coverImageUrl ? (
                      <img
                        src={event.coverImageUrl}
                        alt=""
                        className="size-20 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="flex size-20 shrink-0 items-center justify-center rounded bg-muted">
                        <CalendarDays className="size-7 stroke-[1.5] text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {formatCrmEventDate(event)} · {event.cityName}
                      </p>
                      {canOpenEventAdmin ? (
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: event.id }}
                          className="block text-lg font-medium hover:underline"
                        >
                          {event.title}
                        </Link>
                      ) : (
                        <p className="text-lg font-medium">{event.title}</p>
                      )}
                      {event.organizer ? (
                        <button
                          type="button"
                          className="break-words text-left text-primary hover:underline"
                          onClick={() => setSelected(event.id)}
                          aria-label={`Открыть организатора ${event.organizer?.displayName}`}
                        >
                          {event.organizer.displayName}
                        </button>
                      ) : (
                        <p className="text-sm text-muted-foreground">Организатор не указан</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {selectedId ? (
            <CrmOrganizerPanel key={selectedId} eventId={selectedId} onClose={() => setSelected(null)} />
          ) : null}
        </div>
      )}
    </div>
  );
}
